<script setup lang="ts">
import { ref, watch, watchEffect } from "vue";
import { ColorPattern } from "@zyzgroup/core-web";
import { context, mapState, getColors } from "../../context/context";

// for merge ce shadow-root styles
defineOptions({
  customOptions: {}
});

export type BooleanBoxModePattern =
  | "primary-rect"
  | "primary-round"
  | "transparent-rect"
  | "transparent-round"
  | "double-round";

const props = withDefaults(
  defineProps<{
    mode?: BooleanBoxModePattern;
    colorPattern?: ColorPattern;
    selected?: boolean;
    disabled?: boolean;
  }>(),
  {
    mode: "primary-round",
    colorPattern: context.store.getState().color
      .colorPattern as unknown as ColorPattern,
    selected: false,
    disabled: false
  }
);

const emits = defineEmits<{
  (event: "change", value: boolean): void;
}>();

const realSelected = ref(props.selected);
const realDisabled = ref(props.disabled);

const transparentColor = "rgba(0,0,0,0)";

const backgroundColor = ref(transparentColor);
const outlineColor = ref(transparentColor);
const contentColor = ref(transparentColor);

function changeColor() {
  const { primaryPattern, foregroundPattern, gapPattern } = getColors(
    props.colorPattern
  );

  switch (props.mode) {
    case "primary-round":
    case "primary-rect":
      backgroundColor.value = realDisabled.value
        ? gapPattern
        : realSelected.value
        ? primaryPattern
        : foregroundPattern;
      outlineColor.value =
        realDisabled.value || realSelected.value
          ? transparentColor
          : gapPattern;
      contentColor.value = foregroundPattern;
      break;
    case "transparent-round":
    case "transparent-rect":
      backgroundColor.value = realDisabled.value
        ? gapPattern
        : foregroundPattern;
      outlineColor.value = gapPattern;
      contentColor.value =
        realSelected.value && !realDisabled.value
          ? primaryPattern
          : foregroundPattern;
      break;
    case "double-round":
      backgroundColor.value = foregroundPattern;
      outlineColor.value =
        realSelected.value && !realDisabled.value ? primaryPattern : gapPattern;
      contentColor.value = realDisabled.value
        ? gapPattern
        : primaryPattern;
      break;
  }
}

const mappedState = mapState(["theme.theme"]);
let currentTheme;
watchEffect(() => {
  const nextTheme = mappedState["theme.theme"].value;
  if (currentTheme == nextTheme) {
    return;
  }
  currentTheme = nextTheme;
  changeColor();
});

watch(
  () => [props.mode],
  () => {
    changeColor();
  },
  { immediate: true }
);

const click = () => {
  if (realDisabled.value) {
    return;
  }
  toggleValue();
  emits("change", realSelected.value);
};

// 被动变化
const toggleValue = () => {
  realSelected.value = !realSelected.value;
  changeColor();
};

// 被动变化
const forceValue = (v: boolean) => {
  realSelected.value = v;
  changeColor();
};

defineExpose({ toggleValue, forceValue });
</script>

<template>
  <div
    class="boolean-box"
    :class="realDisabled ? 'disabled' : ''"
    @click="click"
  >
    <slot
      :selected="realSelected"
      :disabled="realDisabled"
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        class="icon"
      >
        <rect
          v-if="
            props.mode == 'primary-rect' || props.mode == 'transparent-rect'
          "
          x="2"
          y="2"
          width="20"
          height="20"
          rx="4"
          strke-width="2"
          :stroke="outlineColor"
          :fill="backgroundColor"
        />
        <circle
          v-if="
            props.mode == 'primary-round' || props.mode == 'transparent-round'
          "
          cx="12"
          cy="12"
          r="10"
          strke-width="2"
          :stroke="outlineColor"
          :fill="backgroundColor"
        />
        <path
          v-if="
            realSelected &&
            (props.mode == 'primary-rect' ||
              props.mode == 'transparent-rect' ||
              props.mode == 'primary-round' ||
              props.mode == 'transparent-round')
          "
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M17.7301 8.26776C17.3702 7.91075 16.7843 7.91075 16.4198 8.26776L10.1961 14.4309C9.83621 14.7879 9.83621 15.3704 10.1961 15.7274C10.5559 16.0844 11.1419 16.0844 11.5063 15.7274L17.7301 9.56428C18.09 9.20726 18.09 8.62947 17.7301 8.26776Z"
          :fill="contentColor"
        />
        <path
          v-if="
            realSelected &&
            (props.mode == 'primary-rect' ||
              props.mode == 'transparent-rect' ||
              props.mode == 'primary-round' ||
              props.mode == 'transparent-round')
          "
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M11.5063 14.431L7.58016 10.5415C7.2203 10.1845 6.63437 10.1845 6.2699 10.5415C5.91003 10.8985 5.91003 11.481 6.2699 11.838L10.2007 15.7322C10.5606 16.0893 11.1465 16.0893 11.511 15.7322C11.8708 15.3705 11.8708 14.7927 11.5063 14.431Z"
          :fill="contentColor"
        />

        <!-- double-round -->
        <circle
          v-if="props.mode == 'double-round'"
          cx="12"
          cy="12"
          r="9"
          :fill="backgroundColor"
          :stroke="outlineColor"
          stroke-width="2"
        />
        <circle
          v-if="realSelected && props.mode == 'double-round'"
          cx="12"
          cy="12"
          r="6"
          :fill="contentColor"
        />
      </svg>
    </slot>
  </div>
</template>

<style scoped>
.boolean-box {
  position: relative;
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
}
.boolean-box.disabled {
  cursor: not-allowed;
  /* pointer-events: none; */
}
.boolean-box .icon {
  flex: 0 0 auto;
  box-sizing: border-box;
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  transition: border-color 0.2s ease-in-out, background-color 0.2s ease-in-out;
}
</style>
